// Helpers de autenticação reutilizados nas páginas da aplicação.
// Espelha a forma como o CadastroEmpresa expõe identidade do usuário, nível
// de acesso e helpers de cache em sessionStorage. Mantido em /frontend/js
// para que páginas mais profundas consigam importar sem ciclos.

import { jwtDecode } from "jwt-decode";

const PAGINA_BLOQUEIO = "/404";

function valorBooleanoAtivo(valor) {
  const normalizado = String(valor || "")
    .trim()
    .toLowerCase();
  return normalizado === "1" || normalizado === "true" || normalizado === "on";
}

function decodificarPayloadJwt(token) {
  try {
    return jwtDecode(String(token || ""));
  } catch {
    return null;
  }
}

/**
 * Variante "safe" usada por outras partes da aplicação (ex.: utils/session.js
 * da página de Acompanhamento) que precisam do payload JWT sem acoplar
 * diretamente ao `jwt-decode`.
 */
export function decodificarPayloadJwtSafe(token) {
  return decodificarPayloadJwt(token);
}

export function getToken() {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem("token");
}

export function getNivelAcesso() {
  // 1. Prioridade: JWT (não pode ser manipulado no client).
  const payload = decodificarPayloadJwt(getToken()) || {};
  const nivelToken = Number(
    payload?.tipo_acesso ??
      payload?.nivel_acesso ??
      payload?.role_id ??
      payload?.role ??
      0,
  );

  if (Number.isFinite(nivelToken) && nivelToken > 0) return nivelToken;

  // 2. Fallback: localStorage (se o JWT não trouxer nível).
  let usuario = {};
  try {
    usuario = JSON.parse(window.localStorage.getItem("usuario") || "{}");
  } catch {
    usuario = {};
  }

  const nivelUsuario = Number(
    usuario?.tipo_acesso ?? usuario?.nivel_acesso ?? 0,
  );

  if (Number.isFinite(nivelUsuario) && nivelUsuario > 0) return nivelUsuario;

  return 0;
}

export function parseTokenPayload() {
  return decodificarPayloadJwt(getToken()) || {};
}

export function isLogado() {
  return !!getToken();
}

export function obterDocumentoUsuarioLogado() {
  if (typeof window === "undefined") return "";

  let usuario = {};
  try {
    usuario = JSON.parse(window.localStorage.getItem("usuario") || "{}");
  } catch {
    usuario = {};
  }

  const payload = decodificarPayloadJwt(getToken()) || {};

  const candidatos = [
    window.localStorage.getItem("documento_empresa"),
    window.localStorage.getItem("empresa_documento"),
    window.localStorage.getItem("empresa_cnpj"),
    window.localStorage.getItem("empresa_cpf"),
    usuario?.empresa?.documento,
    usuario?.empresa?.cnpj,
    usuario?.empresa?.cpf,
    usuario?.empresa_vinculada?.documento,
    usuario?.empresa_vinculada?.cnpj,
    usuario?.empresa_vinculada?.cpf,
    window.localStorage.getItem("documento_usuario"),
    usuario?.documento,
    usuario?.cnpj,
    usuario?.cpf,
    payload?.documento,
    payload?.cnpj,
    payload?.cpf,
    payload?.sub,
  ];

  const encontrado = candidatos.find((valor) => String(valor || "").trim());
  return String(encontrado || "").trim();
}

export function obterUsuarioIdLogado() {
  if (typeof window === "undefined") return null;

  let usuario = {};
  try {
    usuario = JSON.parse(window.localStorage.getItem("usuario") || "{}");
  } catch {
    usuario = {};
  }

  const payload = decodificarPayloadJwt(getToken()) || {};

  const candidatos = [
    window.localStorage.getItem("usuario_id"),
    window.localStorage.getItem("id_usuario"),
    window.localStorage.getItem("user_id"),
    usuario?.id,
    usuario?.usuario_id,
    usuario?.id_usuario,
    usuario?.idUsuario,
    payload?.id,
    payload?.usuario_id,
    payload?.id_usuario,
    payload?.idUsuario,
    payload?.user_id,
    payload?.sub,
  ];

  const encontrado = candidatos.find((valor) => String(valor || "").trim());
  return encontrado ? String(encontrado).trim() : null;
}

export function obterIdentidadeSessao() {
  return {
    nivel: getNivelAcesso(),
    usuarioId: obterUsuarioIdLogado(),
    documento: obterDocumentoUsuarioLogado(),
  };
}

export function logout(navigate) {
  if (typeof window === "undefined") return;

  // Limpa todo o estado persistente para evitar vazamento entre sessões.
  try {
    window.localStorage.clear();
  } catch {}
  try {
    window.sessionStorage.clear();
  } catch {}

  if (navigate) navigate("/login", { replace: true });
  else window.location.replace("/login");
}

/**
 * Reseta todos os campos de formulário visíveis na página. Auxiliar usado
 * no carregamento inicial para evitar que valores antigos sejam
 * reaproveitados via bfcache.
 */
export function garantirPaginaLimpaAoCarregar() {
  if (typeof document === "undefined") return;

  try {
    document.querySelectorAll("form").forEach((form) => {
      try {
        form.reset();
      } catch {}
    });

    document.querySelectorAll("input, textarea, select").forEach((el) => {
      try {
        if (el.type === "checkbox" || el.type === "radio") {
          el.checked = false;
        } else if (el.tagName === "SELECT") {
          el.selectedIndex = el.options?.length > 0 ? 0 : -1;
          el.value = "";
        } else {
          el.value = "";
        }
        el.setAttribute("autocomplete", "off");
        el.classList.remove("is-invalid", "is-valid");
      } catch {}
    });
  } catch {}
}

export { valorBooleanoAtivo, PAGINA_BLOQUEIO };
